import { useEffect, useState, useMemo, useRef } from "react";
import { axiosRequest } from "../helpers/axios";
import { ApiCoursesResponse, FilterData, FilterObj, Section } from "../interfaces/course";
import { AxiosResponse } from "axios";
import { getFilterPrefix, processData, transformedArrayForFilters } from "../helpers/filters";
import { netflixFilterChosen, netflixFilterCleared } from "../components/mixPanelEvents/mixPanelEvents";

declare global {
    interface Window {
        popularCoursesPriority: any;
    }
 }

export const useCourses = (): [FilterData[], boolean, Section[], (filterType: string, isChecked: boolean) => void, (filterType: string, category: string, isChecked: boolean) => void, Record<string, string[]>, () => void, (filterType: string, category: string) => void, Section, Section, (filterType: string, category: string) => void] => {
    const [filterData, setFilterData] = useState<FilterData[]>([]);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [courses, setCourses] = useState<Section[]>([]);
    const [popularCourses, setPopularCourses] = useState<Section | null>();
    const [recomendedCourses, setRecomendedCourses] = useState<Section | null>();
    const [isCheckboxDisabled, setIsCheckboxDisabled] = useState(false);
    const [initialFilterData, setInitialFilterData] = useState<FilterData[]>([]);
    const isFilterDataInitialized = useRef(false);

    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});

    useEffect(() => {
        const getAllFetchedData = async () => {
            await axiosRequest("GET", '/wp-json/pg/v2/courses').then((response: AxiosResponse<ApiCoursesResponse>) => {
                const { data } = response.data;

                setLoaded(true);
                if(window.popularCoursesPriority){
                    [data.sections[0], data.sections[1]] = [data.sections[1], data.sections[0]];
                }
                setCourses(data.sections);
                setPopularCourses(data.sections[1]);
                setRecomendedCourses(data.sections[2]);

                if (!isFilterDataInitialized.current) {
                    const { courseType, instructors, purchased, productType } = processData(data.sections);
                    const originalFilters = [
                        { filterType: "Course Type", data: transformedArrayForFilters(courseType) },
                        { filterType: "Instructor", data: transformedArrayForFilters(instructors) },
                        { filterType: "Product Type", data: transformedArrayForFilters(productType) },
                        { filterType: "Purchased", data: transformedArrayForFilters(purchased) }
                    ];
                    setInitialFilterData(originalFilters);
                    // Initialize selectedFilters based on initialFilterData
                    const initialFilters: Record<string, string[]> = {};
                    originalFilters.forEach(filter => {
                        initialFilters[filter.filterType] = [];
                    });
                    setSelectedFilters(initialFilters);
                    setCourseTypeInStorage(originalFilters[0]);
                    isFilterDataInitialized.current = true; // Mark as initialized
                }
            }).catch(error => console.error(`Error: ${error}`)).finally(() => setLoaded(true));
        };
        getAllFetchedData();
    }, []);

    const setCourseTypeInStorage = (filterData: any) => {
        localStorage.setItem(`userTopics${(window as any).pgUser.ID}`, JSON.stringify(filterData));
    };

    const filteredCourses = useMemo(() => {
        let filtered = [...courses];
        Object.keys(selectedFilters).forEach(filterType => {
            selectedFilters[filterType].forEach((filter) => {
                filtered = filtered.map(section => ({
                    ...section,
                    things: section.things.filter(thing => thing.filters.includes(getFilterPrefix(filterType) + `${filter}`))
                }));
            });
        });
        return filtered;
    }, [courses, selectedFilters]);

    //old memoized data
    // const memoizedFilterData = useMemo(() => {
    //     const { courseType, instructors, purchased } = processData(filteredCourses);

    //     return [
    //         { filterType: "Course Type", data: transformedArrayForFilters(courseType) },
    //         { filterType: "Instructor", data: transformedArrayForFilters(instructors) },
    //         { filterType: "Purchased Options", data: transformedArrayForFilters(purchased) }
    //     ];
    // }, [filteredCourses]);

    const memoizedFilterData = useMemo(() => {
        const { courseType, instructors, purchased, productType } = processData(filteredCourses);
    
        const dynamicFilters = [
            { filterType: "Course Type", data: transformedArrayForFilters(courseType) },
            { filterType: "Instructor", data: transformedArrayForFilters(instructors) },
            { filterType: "Product Type", data: transformedArrayForFilters(productType) },
            { filterType: "Purchased", data: transformedArrayForFilters(purchased) },
        ];
    
        return initialFilterData.map((originalFilter) => {
            // match the filters to the original filters
            const matchingFilter = dynamicFilters.find((filter) => filter.filterType === originalFilter.filterType);
    
            if (matchingFilter) {
                // Reorder the `data` in `matchingFilter` to match the order of `originalFilter.data`
                const reorderedData = originalFilter.data
                    .map((originalItem) =>
                        matchingFilter.data.find((dynamicItem) => dynamicItem.category === originalItem.category)
                    )
                    .filter((item): item is FilterObj => item !== undefined);
    
                return { ...originalFilter, data: reorderedData };
            }
    
            // return empty
            return { ...originalFilter, data: [] };
        });
    }, [filteredCourses, initialFilterData]);
    

    // console.log("memoized is >>> ", memoizedFilterData)

    useEffect(() => {
        setFilterData(memoizedFilterData);
    }, [memoizedFilterData]);

    const handleResetFilter = () => {
        netflixFilterCleared(selectedFilters);
        const resetFilters: Record<string, string[]> = {};
        initialFilterData.forEach(filter => {
            resetFilters[filter.filterType] = [];
        });
        setSelectedFilters(resetFilters);
    };

    const handleFilterTypeCheckboxChange = (filterType: string, isChecked: boolean, isMobile?: boolean) => {
        if (isMobile) {
            setSelectedFilters(prev => {
                const updatedFilters = { ...prev };
                updatedFilters[filterType] = [];
                return updatedFilters;
            });
        }
        setSelectedFilters(prev => {
            const updatedFilters = { ...prev };
            if (isChecked) {
                updatedFilters[filterType] = [];
            }
            return updatedFilters;
        });
       
    };

    const handleFilterCheckboxChange = (filterType: string, category: string, isChecked: boolean) => {
        if (isCheckboxDisabled) return;

        setSelectedFilters(prev => {
            const updatedFilters = { ...prev };
            if (isChecked) {
                if (updatedFilters[filterType]) {
                    updatedFilters[filterType].push(category);
                } else {
                    updatedFilters[filterType] = [category];
                }
                netflixFilterChosen(filterType, category, updatedFilters);
            } else {
                updatedFilters[filterType] = updatedFilters[filterType].filter(c => c !== category);
                netflixFilterCleared(updatedFilters, category, filterType);
            }
            return updatedFilters;
        });
    };

    const handleFilterCheckboxChangeOnMobile = (filterType: string, category: string) => {
        setSelectedFilters(prev => {
            const updatedFilters = { ...prev };
            if (!updatedFilters[filterType]) {
                updatedFilters[filterType] = [];
            }

            const currentIndex = updatedFilters[filterType].indexOf(category);
            if (currentIndex === -1) {
                updatedFilters[filterType].push(category);
                netflixFilterChosen(filterType,category,updatedFilters);
            } else {
                updatedFilters[filterType].splice(currentIndex, 1);
                netflixFilterCleared(updatedFilters,category,filterType)
            }
            return updatedFilters;
        });
    };

    const handleMobileFilterChange = (filterType: string, category: string) => {
        setSelectedFilters(prev => {
            const updatedFilters = { ...prev };
            
            if (updatedFilters[filterType]) {
                if (updatedFilters[filterType].includes(category)) {
                    updatedFilters[filterType] = updatedFilters[filterType].filter(c => c !== category);
                } else {
                    updatedFilters[filterType].push(category);
                }
            } else {
                updatedFilters[filterType] = [category];
            }
            
            return updatedFilters;
        });
    };

    return [filterData, loaded, filteredCourses, handleFilterTypeCheckboxChange, handleFilterCheckboxChange, selectedFilters, handleResetFilter, handleMobileFilterChange, popularCourses!, recomendedCourses!, handleFilterCheckboxChangeOnMobile];
};
